import { createReducer } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { omit } from 'lodash-es';
import {
  apiRequestFailed,
  apiRequestSuccess,
  invalidateLoadedResourceIfPresent,
  markRequestAsPending,
  requestDataIfNecessary,
} from './api.actions';
import {
  ApiRequestFailedInfo,
  ApiRequestInfo,
  ApiRequestSuccessInfo,
  RequestConfig,
} from './api.types';

const initialState = {
  loaded: {} as Record<string, ApiRequestSuccessInfo>,
  pending: {} as Record<string, ApiRequestInfo>,
  failed: {} as Record<string, ApiRequestFailedInfo>,
};

const extractRelevantRequestInfo = ({
  url,
  method,
  headers,
  params: paramsBase,
  data,
}: RequestConfig) => {
  const params =
    paramsBase instanceof URLSearchParams
      ? Object.fromEntries(paramsBase.entries())
      : paramsBase;

  return {
    url,
    method: method || 'GET',
    headers,
    params,
    data,
  };
};

const extractRelevantResponseInfo = (
  response: AxiosResponse | undefined,
  persistData?: boolean,
) => {
  if (!response) {
    return undefined;
  }

  return {
    headers: response.headers,
    status: response.status,
    data: persistData ? response.data : undefined,
  };
};

const apiReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(markRequestAsPending, (state, action) => {
      const {
        payload: {
          resourceId,
          requestConfig: { url },
        },
      } = action;
      state.pending[resourceId] = {
        url,
        resourceId,
        timestamp: new Date().toISOString(),
      };
    })
    .addCase(requestDataIfNecessary, (state, action) => {
      const {
        payload: { resourceId, invalidateIfPresent = false },
      } = action;

      // The resource is loaded.
      // We fetch it again only if it is not invalidated or we are not forcing
      // its invalidation.
      if (state.loaded[resourceId]) {
        if (invalidateIfPresent) {
          state.loaded[resourceId].didInvalidate = true;
        }
      }

      // Remove from failed if present.
      state.failed = omit(state.failed, [resourceId]);
    })
    .addCase(apiRequestFailed, (state, action) => {
      const {
        payload: {
          resourceId,
          requestConfig,
          error,
          persistResponseDataInsideApiReducer,
        },
      } = action;
      const { url } = requestConfig;

      state.pending = omit(state.pending, [resourceId]);

      // https://github.com/axios/axios#handling-errors
      state.failed[resourceId] = {
        url,
        resourceId,
        timestamp: new Date().toISOString(),
        request: extractRelevantRequestInfo(requestConfig),
        response: extractRelevantResponseInfo(
          error?.response,
          persistResponseDataInsideApiReducer,
        ),
        errorMessage: error?.message,
      };
    })
    .addCase(apiRequestSuccess, (state, action) => {
      const {
        payload: {
          resourceId,
          requestConfig,
          response,
          persistResponseDataInsideApiReducer,
        },
      } = action;
      const { url } = requestConfig;

      state.pending = omit(state.pending, [resourceId]);
      state.failed = omit(state.failed, [resourceId]);

      state.loaded[resourceId] = {
        url,
        resourceId,
        timestamp: new Date().toISOString(),
        didInvalidate: false,
        request: extractRelevantRequestInfo(requestConfig),
        response: extractRelevantResponseInfo(
          response,
          persistResponseDataInsideApiReducer,
        )!,
      };
    })
    .addCase(invalidateLoadedResourceIfPresent, (state, action) => {
      const {
        payload: { resourceId },
      } = action;

      if (state.loaded[resourceId]) {
        state.loaded[resourceId].didInvalidate = true;
      }
    }),
);

export default apiReducer;
